/**
 * Utility functions for pSEO Template
 * Following RULES.md: calculateDynamicFontSize, formatDate, etc.
 */

/**
 * Calculate dynamic font size based on digit count.
 * Ensures ALL digits remain visible — no ellipsis, no overflow hidden.
 */
export function calculateDynamicFontSize(value, options = {}) {
  const {
    baseFontSize = 1.8,
    minFontSize = 0.6,
    maxDigits = 4,
    scaleFactor = 0.2,
  } = options;

  if (value === null || value === undefined) return `${baseFontSize}rem`;

  const numStr = String(Math.abs(Number(value))).replace(/[^0-9]/g, "");
  const digitCount = numStr.length;

  if (digitCount <= maxDigits) return `${baseFontSize}rem`;

  const excess = digitCount - maxDigits;
  let reduction = excess * scaleFactor;

  // Extra reduction for 6-digit values (card padding on tablet)
  if (digitCount === 6) reduction += 0.1;

  // Exponential scaling for very large numbers
  if (digitCount >= 8) reduction += (digitCount - 7) * 0.05;

  const fontSize = Math.max(minFontSize, baseFontSize - reduction);
  return `${fontSize.toFixed(2)}rem`;
}

/**
 * Format a date string
 */
export function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Format phone number for display
 */
export function formatPhone(phone) {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

/**
 * Format phone number for tel: link
 */
export function phoneHref(phone) {
  if (!phone) return "";
  return `tel:+1${phone.replace(/\D/g, "")}`;
}

/**
 * Generate a slug from text
 */
export function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Get service data from taxonomy by slug
 */
export function getServiceBySlug(taxonomy, slug) {
  const serviceDim = taxonomy.dimensions.find((d) => d.id === "service");
  return serviceDim?.values.find((v) => v.slug === slug) || null;
}

/**
 * Get location data from taxonomy by slug
 */
export function getLocationBySlug(taxonomy, slug) {
  const locationDim = taxonomy.dimensions.find((d) => d.id === "location");
  return locationDim?.values.find((v) => v.slug === slug) || null;
}

/**
 * Get all services for a given category
 */
export function getServicesByCategory(taxonomy, categoryId) {
  const serviceDim = taxonomy.dimensions.find((d) => d.id === "service");
  return serviceDim?.values.filter((v) => v.category === categoryId) || [];
}

/**
 * Get all locations for a given brand
 */
export function getLocationsByBrand(taxonomy, brandKey) {
  const locationDim = taxonomy.dimensions.find((d) => d.id === "location");
  return locationDim?.values.filter((v) => v.brand === brandKey) || [];
}

/**
 * Get all locations for a given state
 */
export function getLocationsByState(taxonomy, state) {
  const locationDim = taxonomy.dimensions.find((d) => d.id === "location");
  return locationDim?.values.filter((v) => v.state === state) || [];
}

/**
 * Generate cross-links for a service page
 * Returns other services in same city + same service in other cities
 */
export function generateCrossLinks(taxonomy, serviceSlug, locationSlug) {
  const services = taxonomy.dimensions.find((d) => d.id === "service")?.values || [];
  const locations = taxonomy.dimensions.find((d) => d.id === "location")?.values || [];
  const currentLocation = locations.find((l) => l.slug === locationSlug);

  // Other services in same city (same brand)
  const otherServices = services
    .filter((s) => s.slug !== serviceSlug)
    .slice(0, 6)
    .map((s) => ({
      path: `/${locationSlug}/${s.slug}`,
      label: `${s.display} in ${currentLocation?.city || locationSlug}`,
    }));

  // Same service in other cities (same brand)
  const otherLocations = locations
    .filter((l) => l.slug !== locationSlug && l.brand === currentLocation?.brand)
    .map((l) => ({
      path: `/${l.slug}/${serviceSlug}`,
      label: `${services.find((s) => s.slug === serviceSlug)?.display || serviceSlug} in ${l.city}`,
    }));

  return { otherServices, otherLocations };
}
